import React, { useState, useMemo } from "react";
import Modal from "components/Modal";
import { useModalStore, Modal as ModalKey } from "stores/modal/useModalStore";
import { useHorseStore } from "stores/horse/useHorseStore";
import { FormProvider, useForm } from "react-hook-form";
import { GoogleApiWrapper } from "google-maps-react";
import { useMutation, useQuery } from "@apollo/client";
import { LIST_MY_CUSTOM_SADDLE_ORDERS } from "apollo/mutations/customSaddleOrderRequest";
import { CUSTOM_TREE_CHECKOUT } from "apollo/mutations/customRequestCheckout";
import ModalStore from "components/Modal/ModalStore";
import Step1HorseInfo from "./Step1HorseInfo";
import Step2SaddleStyle from "./Step2SaddleStyle";
import Step3SaddleSpecifications from "./Step3SaddleSpecifications";
import Step4ShippingDestination from "./Step4ShippingDestination";
import Step5ReviewSubmit from "./Step5ReviewSubmit";
import Button from "components/Buttons";

const TOTAL_STEPS = 5;

// TODO (meeting req): Add multi-horse dropdown when user has multiple scanned horses.
// Currently horseData comes from pre-selected card; add Step1 horse selector for multi-horse users.
export const CustomSaddleRequestModal = () => {
  const close = useModalStore((s) => s.close);
  const current = useModalStore((s) => s.current);
  const horseData = useHorseStore((state) => state.horseData);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmittingLocked, setIsSubmittingLocked] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fetch existing custom saddle orders
  const { data: ordersData } = useQuery(LIST_MY_CUSTOM_SADDLE_ORDERS, {
    fetchPolicy: "network-only",
  });

  const [customTreeCheckout, { loading: isSubmitting }] = useMutation(CUSTOM_TREE_CHECKOUT);

  // Check if there's already a pending order for this horse
  const hasPendingOrder = useMemo(() => {
    if (!ordersData?.listMyCustomSaddleOrders?.items || !horseData?.id) return false;
    return ordersData.listMyCustomSaddleOrders.items.some(
      (order) => order.status === "PENDING" && order.horseName === horseData?.name
    );
  }, [ordersData, horseData]);

  const isOpen = current === ModalKey.RequestCustomSaddle;

  const methods = useForm({
    defaultValues: {
      saddleType: "WESTERN",
      seatSize: null,
      discipline: null,
      shippingDestination: null,
      shippingAddress: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        suiteOrApt: "",
        country: "USA",
        phone: "",
      },
      manufacturerId: null,
      manufacturerName: null,
      notes: "",
    },
    mode: "onBlur",
  });

  const { handleSubmit, trigger } = methods;

  const handleNext = async () => {
    if (isValidating) return;
    setIsValidating(true);

    let fieldsToValidate = [];

    switch (currentStep) {
      case 1:
        break;
      case 2:
        break;
      case 3:
        fieldsToValidate = ["seatSize", "discipline"];
        break;
      case 4: {
        fieldsToValidate = ["shippingDestination"];
        const shippingDest = methods.getValues("shippingDestination");
        if (shippingDest === "CUSTOMER") {
          fieldsToValidate.push(
            "shippingAddress.street",
            "shippingAddress.city",
            "shippingAddress.state",
            "shippingAddress.zipCode",
            "shippingAddress.phone"
          );
        } else if (shippingDest === "MANUFACTURER") {
          fieldsToValidate.push("manufacturerId");
        }
        break;
      }
      default:
        break;
    }

    if (fieldsToValidate.length > 0) {
      const isValidStep = await trigger(fieldsToValidate);
      if (!isValidStep) {
        setIsValidating(false);
        return;
      }
    }

    if (currentStep < TOTAL_STEPS) {
      if (currentStep + 1 === TOTAL_STEPS) {
        setIsSubmittingLocked(true);
        setTimeout(() => {
          setIsSubmittingLocked(false);
        }, 1000);
      }
      setCurrentStep(currentStep + 1);
    }
    setIsValidating(false);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const extractHorseMeasurements = () => {
    const m = horseData?.measurements;
    if (!m) return null;

    return {
      gulletWidth: m.gulletWidth ?? null,
      barAngle: m.barAngle ?? null,
    };
  };

  const onSubmit = async (data) => {
    if (isSubmitting || isSubmitted) return;

    const measurements = extractHorseMeasurements();
    console.log({ measurements });

    const input = {
      horseId: horseData?.id,
      saddleType: data.saddleType,
      saddleSpecs: {
        seatSize: data.seatSize,
        discipline: data.discipline?.value || data.discipline,
      },
      shippingDestination: data.shippingDestination,
      customerNotes: data.notes || null,
    };

    if (data.shippingDestination === "CUSTOMER") {
      input.shippingAddress = {
        street: data.shippingAddress.street,
        city: data.shippingAddress.city,
        state: data.shippingAddress.state,
        zipCode: data.shippingAddress.zipCode,
        country: data.shippingAddress.country || "USA",
        phone: data.shippingAddress.phone?.replace(/\D/g, "") || "",
      };
    }

    if (data.shippingDestination === "MANUFACTURER") {
      input.manufacturerId = data.manufacturerId;
    }

    console.log("Custom Saddle Request Input:", input);

    try {
      const result = await customTreeCheckout({
        variables: { input },
      });
      console.log("Custom Saddle Order Created with Checkout:", result);

      const sessionURL = result?.data?.customTreeCheckout?.sessionURL;

      // // Refetch orders to update the pending status
      // await refetchOrders();

      setIsSubmitted(true);
      close();
      setCurrentStep(1);
      methods.reset();

      // Redirect to payment session URL
      if (sessionURL) {
        window.location.href = sessionURL;
      } else {
        // Fallback if no sessionURL returned
        ModalStore.openSimpleError({
          title: "Error",
          subtitle: "Failed to create payment session. Please try again.",
          shouldCloseOnOverlayClick: true,
        });
      }

      setIsSubmitted(false);
    } catch (error) {
      console.error("Error creating custom saddle order:", error);
      ModalStore.openSimpleError({
        title: "Error",
        subtitle: error?.message || "Failed to submit your custom saddle request. Please try again.",
        shouldCloseOnOverlayClick: true,
      });
    }
  };

  const handleClose = () => {
    close();
    setCurrentStep(1);
    methods.reset();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1HorseInfo horseData={horseData} />;
      case 2:
        return <Step2SaddleStyle methods={methods} />;
      case 3:
        return <Step3SaddleSpecifications methods={methods} />;
      case 4:
        return <Step4ShippingDestination methods={methods} />;
      case 5:
        return <Step5ReviewSubmit methods={methods} horseData={horseData} />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Modal
        isCloseIcon
        modalIsOpen={isOpen}
        closeModal={handleClose}
        shouldCloseOnOverlayClick={false}
        className="w-full sm:w-[92%] md:w-[85%] lg:w-[800px] max-h-[90vh] overflow-y-auto p-4 sm:p-6 lg:p-10"
        title={
          <span className="block">
            <span className="block sm:inline">Request Custom Saddle</span>
            <span className="block sm:inline sm:ml-1 text-[16px] sm:text-[20px] md:text-[24px] lg:text-[28px]">
              (Step {currentStep} of {TOTAL_STEPS})
            </span>
          </span>
        }
        titleClassName="!text-[18px] sm:!text-[24px] md:!text-[28px] lg:!text-[36px] !leading-tight"
      >
        {{
          content: (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mt-4 sm:mt-6">{renderStep()}</div>

              {/* Progress Indicator */}
              <div className="flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8 mb-4 sm:mb-6">
                {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${index + 1 <= currentStep
                      ? "bg-[#2b364b]"
                      : "bg-gray-300"
                      }`}
                  />
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between gap-2 sm:gap-4 mt-4 sm:mt-6">
                <Button
                  type="button"
                  transparent
                  onClick={currentStep === 1 ? handleClose : handleBack}
                  className="!border-[#2B364B] !text-[#2B364B] !rounded-[30px] !h-[36px] sm:!h-[40px] lg:!h-[42px] !px-3 sm:!px-4 lg:!px-6 font-montserrat !text-xs sm:!text-sm lg:!text-base flex justify-center items-center"
                >
                  {currentStep === 1 ? "Cancel" : "Back"}
                </Button>

                {currentStep < TOTAL_STEPS ? (
                  <Button
                    type="button"
                    primary
                    onClick={handleNext}
                    loading={isValidating}
                    disabled={isValidating}
                    className={`!bg-[#2B364B] !text-white !rounded-[30px] !h-[36px] sm:!h-[40px] lg:!h-[42px] !px-3 sm:!px-4 lg:!px-6 font-montserrat !text-xs sm:!text-sm lg:!text-base flex justify-center items-center ${isValidating ? "opacity-75 cursor-wait" : ""}`}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    primary
                    loading={isSubmitting}
                    disabled={isSubmittingLocked || isSubmitting || isSubmitted || hasPendingOrder}
                    className={`!bg-[#2B364B] !text-white !rounded-[30px] !h-[36px] sm:!h-[40px] lg:!h-[42px] !px-3 sm:!px-4 lg:!px-6 font-montserrat !text-xs sm:!text-sm lg:!text-base flex justify-center items-center ${(isSubmittingLocked || isSubmitting || isSubmitted || hasPendingOrder) ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {hasPendingOrder ? "Request Pending" : "Proceed to Payment"}
                  </Button>
                )}
              </div>
            </form>
          ),
        }}
      </Modal>
    </FormProvider>
  );
};

export default GoogleApiWrapper({
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  LoadingContainer: "div",
})(CustomSaddleRequestModal);
